import { useWindowEventListener } from '../../../../hooks/useWindowEventListener';
import { seenNotificationThunk } from '../../../../store/notification/thunks';
import { AlertIcon, EmptyStateIcon } from '../../../../assets/icons';
import { useDispatch, useSelector } from '../../../../store/store';
import Spinner from '../../../../components/Spinner';
import { MouseEvent, useMemo, useState } from 'react';
import styles from './style.module.scss';
import {
    Notification,
    NotificationsFilter,
} from '../../../../store/notification/types';
import {
    fromNowShort,
    notificationGroup,
    notificationGroups,
} from '../../../../utils/dateHelpers';
import {
    selectSortedNotifications,
    selectFetchingNotifications,
    selectUnreadNotificationsCount,
} from '../../../../store/notification/selectors';

const filters: NotificationsFilter[] = ['All', 'Unread'];

function Notifications() {
    const dispatch = useDispatch();
    const notifications = useSelector(selectSortedNotifications);
    const unreadCount = useSelector(selectUnreadNotificationsCount);
    const fetchingNotifications = useSelector(selectFetchingNotifications);
    const [show, setShow] = useWindowEventListener(closeMenu);
    const [filter, setFilter] = useState<NotificationsFilter>('All');
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const groupedList = useMemo(() => {
        // The open item stays on the unread tab, so reading it does not pull it away mid-read.
        const list = filter === 'Unread'
            ? notifications.filter(({ id, seen }) => !seen || id === expandedId)
            : notifications;

        const groups = notificationGroups.map(title => ({
            title,
            items: [] as Notification[],
        }));

        list.forEach(item => {
            const group = groups.find(({ title }) => title === notificationGroup(item.modify));
            group?.items.push(item);
        });

        return groups.filter(({ items }) => items.length > 0);
    }, [notifications, filter, expandedId]);

    function closeMenu() {
        setShow(false);
    };

    const toggleMenu = () => {
        setShow(prevState => !prevState);
    };

    /** The menu sits inside the header, so its own clicks must not reach the close listener. */
    const keepMenuOpen = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    const itemClick = (item: Notification) => {
        const expanding = expandedId !== item.id;

        setExpandedId(expanding ? item.id : null);
        if (expanding && !item.seen) dispatch(seenNotificationThunk(item.id));
    };

    const emptyStateText = filter === 'Unread'
        ? 'There is no unread notification!'
        : 'There is no notification yet!';

    return (
        <div className={styles.container}>
            <button
                onClick={toggleMenu}
                className={styles.bell}
                data-menu-open={show}
                aria-label='Notifications'
            >
                <AlertIcon />
                {unreadCount > 0 && <span className={styles.bell_badge} />}
            </button>

            {show &&
                <div className={styles.menu} onClick={keepMenuOpen}>
                    <div className={styles.menu_header}>
                        <div className={styles.menu_header_top}>
                            <span className={styles.menu_header_title}>Notifications</span>
                            <div className={styles.menu_header_filters}>
                                {filters.map(item => (
                                    <button
                                        key={item}
                                        onClick={() => setFilter(item)}
                                        data-selected={filter === item}
                                        className={styles.menu_header_filters_item}
                                    >
                                        {item}
                                        {item === 'Unread' && unreadCount > 0 && <span>{unreadCount}</span>}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <hr className={styles.menu_header_divider} />
                    </div>

                    <div className={styles.menu_body}>
                        {fetchingNotifications && !notifications.length
                            ? <div className={styles.loading}><Spinner /></div>
                            : !groupedList.length
                                ? <div className={styles.empty}>
                                    <EmptyStateIcon />
                                    <span>{emptyStateText}</span>
                                </div>
                                : groupedList.map(group => (
                                    <div key={group.title} className={styles.group}>
                                        <span className={styles.group_title}>{group.title}</span>
                                        {group.items.map(item => (
                                            <div
                                                key={item.id}
                                                className={styles.item}
                                                onClick={() => itemClick(item)}
                                                data-expanded={expandedId === item.id}
                                            >
                                                <div className={styles.item_head}>
                                                    {!item.seen && <span className={styles.item_head_dot} />}
                                                    <span className={styles.item_head_title}>{item.title}</span>
                                                    <span className={styles.item_head_time}>{fromNowShort(item.modify)}</span>
                                                </div>
                                                <p className={styles.item_message}>{item.message}</p>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                    </div>
                </div>}
        </div>
    );
};

export default Notifications;
